import React from 'react';
import { apiErrorMessage } from '../hooks/useApi';

/**
 * <ErrorState /> — what a screen or panel shows when its data failed to load.
 * Never let a failed load fall through to an empty state ("No jobs yet").
 *
 *   const { data, error, reload } = useApiData('/api/jobs', []);
 *   if (error) return <ErrorState thing="jobs" error={error} onRetry={reload} />;
 *
 * @param {string} [thing]        what failed: "jobs" → "Couldn't load jobs"
 * @param {ReactNode} [title]     override the heading entirely
 * @param {*} [error]             error from apiFetch/useApiData — turned into a reason via apiErrorMessage()
 * @param {ReactNode} [reason]    explicit reason text (wins over `error`)
 * @param {()=>void} [onRetry]    shows a Retry button
 * @param {boolean} [retrying]    spinner + disabled Retry while reloading
 * @param {boolean} [compact]     inline/card variant (smaller padding, no big icon)
 * @param {ReactNode} [action]    extra action next to Retry
 */
const ErrorState = ({ thing, title, error, reason, onRetry, retrying = false, compact = false, action, className = '' }) => {
  const heading = title || (thing ? `Couldn't load ${thing}` : "Couldn't load this");
  const why = reason || (error ? apiErrorMessage(error) : 'Something went wrong — try again.');
  return (
    <div className={`cw-error-state${compact ? ' compact' : ''} ${className}`.trim()} role="alert">
      <span className="cw-error-state-ico" aria-hidden="true"><i className="fa-solid fa-triangle-exclamation" /></span>
      <div className="cw-error-state-text">
        <p className="cw-error-state-title">{heading}</p>
        <p className="cw-error-state-reason">{why}</p>
      </div>
      {(onRetry || action) && (
        <div className="cw-error-state-actions">
          {onRetry && (
            <button type="button" className="cw-btn cw-btn-secondary" onClick={onRetry} disabled={retrying}>
              <i className={retrying ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-rotate-right'} aria-hidden="true" />
              {retrying ? 'Retrying…' : 'Retry'}
            </button>
          )}
          {action}
        </div>
      )}
    </div>
  );
};

export default ErrorState;
